import { useContext } from 'react'
import { Card, Grid, Text, Stack, Title, Group, NavLink, Skeleton } from '@mantine/core'
import { PageContainer } from '@shared/components/app-shell/page-container/page-container'
import { PAGE_TITLES } from '@/constants/page-titles'
import { StationContext } from '@/pages/station-context'
import classes from './home.module.css'
import { IconZoomScan, IconFileTextSpark, IconTableExport } from '@tabler/icons-react'
import { useNavigation } from '@/hooks/use-navigation'
import { AlertLevel, IconAlert } from '@shared/components/icon-alert/icon-alert'

import { useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { Markdown } from 'tiptap-markdown'

import { RichTextEditor, Link } from '@mantine/tiptap'

import Underline from '@tiptap/extension-underline'

export function HomePage() {
  const { navigateToWoolData, navigateToListSpeci, navigateToListOffers } = useNavigation()
  const { allowedStations, welcomeMessage, isLoading, isError } = useContext(StationContext)

  const editor = useEditor(
    {
      extensions: [
        StarterKit,
        Link,
        Underline,

        Markdown.configure({
          html: true, // Allow HTML in markdown
          tightLists: true,
          bulletListMarker: '-',
          linkify: false,
          breaks: false,
        }),
      ],
      content: welcomeMessage,
      editable: false,
    },
    [welcomeMessage]
  )

  return (
    <PageContainer title={PAGE_TITLES.HOME}>
      <Stack>
        {welcomeMessage && (
          <>
            <Title order={2}>Updates from Zentera</Title>
            <div>
              <Card shadow='sm' radius='md' withBorder style={{ padding: '0' }}>
                <RichTextEditor
                  aria-label='rich-text-editor'
                  editor={editor}
                  style={{ width: '100%', border: 'none' }}
                >
                  <RichTextEditor.Content data-testid='rte-content' />
                </RichTextEditor>
              </Card>
            </div>
          </>
        )}

        <Title order={2}>Your stations</Title>
        <Grid>
          {isLoading &&
            Array.from({ length: 3 }).map((_, index) => (
              <Grid.Col span={{ base: 12, sm: 6, xl: 4 }} key={index}>
                <Skeleton height={180} />
              </Grid.Col>
            ))}
          {allowedStations.length > 0 &&
            !isLoading &&
            !isError &&
            allowedStations.map((station) => (
              <Grid.Col span={{ base: 12, sm: 6, xl: 4 }} key={station.recordNumber}>
                <Card
                  shadow='sm'
                  radius='md'
                  withBorder
                  aria-label={`${station.brand} station summary and links`}
                >
                  <Group justify='space-between' mb='sm'>
                    <Text size='lg' fw={500}>
                      {station.brand}
                    </Text>
                    <Text size='sm' c='dimmed'>
                      {station.recordNumber}
                    </Text>
                  </Group>

                  <Card.Section>
                    <NavLink
                      classNames={{
                        root: classes.navLink,
                      }}
                      key={`${station.recordNumber}-wool-data`}
                      label='Wool data'
                      aria-label={`${station.brand} Wool data`}
                      onClick={() => navigateToWoolData(station.recordNumber)}
                      leftSection={<IconTableExport color='var(--mantine-color-violet-3)' />}
                    />
                    <NavLink
                      classNames={{
                        root: classes.navLink,
                      }}
                      key={`${station.recordNumber}-wool-speci`}
                      label='Wool speci'
                      aria-label={`${station.brand} Wool speci`}
                      onClick={() => navigateToListSpeci(station.recordNumber)}
                      leftSection={<IconZoomScan color='var(--mantine-color-violet-3)' />}
                    />
                    <NavLink
                      classNames={{
                        root: classes.navLink,
                      }}
                      key={`${station.recordNumber}-offers`}
                      label='Offers'
                      aria-label={`${station.brand} Offers`}
                      onClick={() => navigateToListOffers(station.recordNumber)}
                      leftSection={<IconFileTextSpark color='var(--mantine-color-violet-3)' />}
                    />
                  </Card.Section>
                </Card>
              </Grid.Col>
            ))}
        </Grid>
        {allowedStations.length === 0 && !isLoading && !isError && (
          <IconAlert
            level={AlertLevel.INFO}
            title='No stations available'
            message='Your account has no stations associated with it. Please contact your Area Manager or Zentera to resolve this issue if you need station access.'
          />
        )}
        {isError && (
          <IconAlert
            level={AlertLevel.ERROR}
            title='Error loading stations'
            message='Please try again later.'
          />
        )}
      </Stack>
    </PageContainer>
  )
}
